import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import Overlay from "../../../Games/Shared/Overlay/Overlay";
import HoldAndWinOutroController from "./HoldAndWinOutroController";
import TextButton from "../../UserInterface/primary/Buttons/TextButton";
import { safeInit } from "../../../utilities";
import {
  selectShowHoldAndWinOutro,
  selectHoldAndWinCurrentRound,
  selectBet,
  selectAutoPlaySpeed,
} from "../../../State/selectors";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

const HoldAndWinOutroDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showHoldAndWinOutro = useSelector(selectShowHoldAndWinOutro);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const bet = useSelector(selectBet);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);

  const outroControllerRef = useRef(null);
  const spineContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const buttonRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, symbolHeight, device } = dimensionsContext;

  // Determine button text based on device type
  const continueButtonKey = useMemo(() => {
    return device === PixiSettings.DEVICE.DESKTOP
      ? "click_to_continue"
      : "tap_to_continue";
  }, [device]);

  // Initialize controller once
  useEffect(() => {
    if (spineContainerRef.current && !outroControllerRef.current) {
      // Set button alpha to 0 for this instance
      if (buttonRef.current) {
        buttonRef.current.alpha = 0;
      }

      safeInit(() => {
        outroControllerRef.current = new HoldAndWinOutroController(
          spineContainerRef.current,
          dimensionsContext,
          dispatch,
          symbolHeight,
          overlayRef,
          buttonRef,
          t
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (outroControllerRef.current && symbolHeight && dimensionsContext) {
      outroControllerRef.current.updateDimensions(
        dimensionsContext,
        symbolHeight
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight]);

  // Play animation when triggered
  useEffect(() => {
    if (outroControllerRef.current && showHoldAndWinOutro) {
      // Get win value and bet from state
      const winValue = holdAndWinCurrentRound?.win || 0;
      const betValue = bet || 0;
      const isTurboMode = autoPlaySpeed === "turbo";
      outroControllerRef.current.startOutroAnimation(winValue, betValue, isTurboMode);
    }
  }, [showHoldAndWinOutro, holdAndWinCurrentRound, bet, autoPlaySpeed]);

  // Handle user click to continue animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (outroControllerRef.current && showHoldAndWinOutro) {
        const accepted = outroControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showHoldAndWinOutro]
  );

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle spacebar
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat (holding the key)
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      // Only handle when outro is showing AND controller exists
      if (!showHoldAndWinOutro || !outroControllerRef.current) {
        return;
      }

      // Prevent event from reaching other handlers
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = outroControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch (error) {
        // Emergency recovery - try to finish animation
        if (
          outroControllerRef.current &&
          outroControllerRef.current.finishAnimation
        ) {
          outroControllerRef.current.finishAnimation();
        }
      }
    };

    // Use capture phase to ensure we get the event first
    if (showHoldAndWinOutro) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false, // Allow preventDefault
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showHoldAndWinOutro]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (outroControllerRef.current) {
        outroControllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_outro"
      x={dimensions.width / 2}
      y={dimensions.height / 2}
      sortableChildren={true}
      zIndex={9}
      ref={spineContainerRef}
    >
      <Overlay
        ref={overlayRef}
        dimensions={dimensions}
        interactive={showHoldAndWinOutro}
        onPointerDown={handleBackgroundClick}
        x={-dimensions.width / 2}
        y={-dimensions.height / 2}
        fadeDuration={350}
      />

      <TextButton
        ref={buttonRef}
        y={dimensions.height / 2}
        translationKey={continueButtonKey}
      />
    </Container>
  );
};

export default HoldAndWinOutroDialog;
